import React, { useContext, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Link
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// The app's established "a clinician has looked at this and stands behind it"
// glyph — the same icon used for a clinically confirmed diagnosis. Shared
// deliberately: the claim is of the same kind, so the symbol should be too.
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import IconButton from '@mui/material/IconButton';
import moment from 'moment';
import { ThemeContext } from 'src/store/ContextStore';
import { formatDateForDisplay } from 'src/hooks/useDateFormatter';
import { toLocalDateFromPickerOrImport } from 'src/util/partialDate';
import {
  paleChipSx,
  severityFromInterpretation
} from 'src/components/UI/chips/paleChipSx';
import {
  variantSources,
  hasClassificationConflict,
  discrepancyReview
} from 'src/util/variantPathogenicity';
import DiscrepancyReviewDialog from './DiscrepancyReviewDialog';
import { VARIANT_INTERPRETATION_OPTIONS } from '../clinical-notes-section/forms/formService';
import { TILE_COLOURS } from '../tileColours';
import { useAutoTranslation, translate } from 'src/hooks/useTranslate';
import { useMonthDate } from 'src/i18n/dates';
import { classificationText } from './classificationText';

const HGVS_FIELDS = [
  { key: 'variantG', prefix: 'g.' },
  { key: 'variantC', prefix: 'c.' },
  { key: 'variantP', prefix: 'p.' }
];

const renderHgvs = (variant) => {
  const parts = HGVS_FIELDS.map(({ key, prefix }) => {
    const raw = variant[key];
    if (!raw) return null;
    return raw.startsWith(prefix) ? raw : `${prefix}${raw}`;
  }).filter(Boolean);

  if (parts.length === 0)
    return variant.gene || translate('Unspecified variant');
  return parts.join(' · ');
};

// One tooltip, three states. The unresolved and stale cases must not read the
// same: a stale review means someone DID look, and what they looked at has
// since moved.
const discrepancyTooltip = (review, dateFormat) => {
  if (review.reviewed) {
    const who = review.current?.reviewedBy;
    const when = review.current?.reviewedAt
      ? moment(review.current.reviewedAt).format(dateFormat)
      : null;
    // One sentence for each thing the review states.
    if (who && when)
      return translate(
        'Laboratory and ClinVar classifications differ. Reviewed by {{who}} on {{when}}. Click to view or review again.',
        { who, when }
      );
    if (who)
      return translate(
        'Laboratory and ClinVar classifications differ. Reviewed by {{who}}. Click to view or review again.',
        { who }
      );
    if (when)
      return translate(
        'Laboratory and ClinVar classifications differ. Reviewed on {{when}}. Click to view or review again.',
        { when }
      );
    return translate(
      'Laboratory and ClinVar classifications differ. Reviewed. Click to view or review again.'
    );
  }
  if (review.stale) {
    return translate(
      'Laboratory and ClinVar classifications differ. A previous review no longer applies because a source or the determination has changed since. Click to review again.'
    );
  }
  return translate(
    'Laboratory and ClinVar classifications differ, and this has not been reviewed. Click to review.'
  );
};

const VariantRow = ({
  variant,
  onClinicianInterpretationChange,
  onReviewDiscrepancy,
  reviewerName,
  onSourceTestClick,
  disabled
}) => {
  const t = useAutoTranslation();

  const { theme } = useContext(ThemeContext);
  const [reviewOpen, setReviewOpen] = useState(false);
  const dateFormat = theme?.dateFormat || 'DD/MM/YYYY';

  // One shared predicate across every surface (pedigree marker, legend, this
  // row) instead of the three subtly different rules that used to disagree —
  // a record could warn here and not on the chart. Bucketed, so Pathogenic vs
  // Likely pathogenic is concordant and only a material disagreement warns.
  const showDiscordance = hasClassificationConflict(variant);
  // A discrepancy that has been explicitly reviewed is a different state from
  // an unresolved one, and from one whose review no longer applies because a
  // source has since changed its mind.
  const review = discrepancyReview(variant);

  const sources = variantSources(variant);
  const labSource = sources.find((s) => s.key === 'lab');
  const clinvarSource = sources.find((s) => s.key === 'clinvar');
  const clinvar = clinvarSource?.provenance || {};

  // sourceTestDate has two writers: the Test form's picker (a local instant) and a confirmed lab
  // report's fill or an imported Test (UTC midnight of the day printed). Each is read by its own
  // clock (TGCLOUD-1866); a local read put a report dated 02/09 on 01/09 west of Greenwich.
  const sourceLabel = [
    variant.sourceTestName,
    variant.sourceTestDate
      ? formatDateForDisplay(
          toLocalDateFromPickerOrImport(variant.sourceTestDate),
          dateFormat
        )
      : ''
  ]
    .filter(Boolean)
    .join(' – ');

  return (
    <Box
      sx={{
        border: `1px solid ${TILE_COLOURS.genotype.border}`,
        borderLeft: `3px solid ${TILE_COLOURS.genotype.accent}`,
        backgroundColor: TILE_COLOURS.genotype.background,
        borderRadius: '6px',
        px: 1.5,
        py: 1,
        mb: 1
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: TILE_COLOURS.genotype.text,
          lineHeight: 1
        }}
      >
        {t('Variant')}
      </Typography>

      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: 13,
            wordBreak: 'break-word'
          }}
        >
          {renderHgvs(variant)}
        </Typography>
        <VariantOriginChip origin={variant.origin} />
      </Box>

      {sourceLabel && (
        <Typography
          variant="caption"
          sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}
        >
          {t('From:')}{' '}
          {variant.sourceTestId ? (
            <Link
              component="button"
              type="button"
              underline="always"
              variant="caption"
              onClick={() => {
                const testId = String(variant.sourceTestId);
                if (onSourceTestClick) {
                  onSourceTestClick({ testId });
                  return;
                }
                window.dispatchEvent(
                  new CustomEvent('open-source-test-record', {
                    detail: { testId }
                  })
                );
              }}
              sx={{ verticalAlign: 'baseline', fontWeight: 500 }}
            >
              {sourceLabel}
            </Link>
          ) : (
            sourceLabel
          )}
        </Typography>
      )}

      <Box
        sx={{
          mt: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.75
        }}
      >
        {/* Each source with its own classification, always present. A
            laboratory that made no call is stated explicitly — that absence is
            clinically meaningful, and hiding the chip is what allowed a
            ClinVar-only assertion to read as the record's own verdict. */}
        <Chip
          size="small"
          variant={labSource?.classification ? 'filled' : 'outlined'}
          label={
            labSource?.classification
              ? t('Laboratory: {{classification}}', {
                  classification: classificationText(
                    labSource.classification,
                    t
                  )
                })
              : t('Laboratory: no classification reported')
          }
          sx={paleChipSx(
            severityFromInterpretation(labSource?.classification || '')
          )}
        />

        <ClinvarChip source={clinvarSource} provenance={clinvar} />

        <ClinicianSelect
          value={variant.clinicianInterpretation || ''}
          disabled={disabled}
          onChange={(value) =>
            onClinicianInterpretationChange(variant.variantId, value)
          }
        />

        {showDiscordance && (
          <Tooltip title={discrepancyTooltip(review, dateFormat)}>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => setReviewOpen(true)}
              aria-label={
                review.reviewed
                  ? t('View clinician review of this discrepancy')
                  : t('Review this classification discrepancy')
              }
              sx={{ p: 0.25 }}
            >
              {review.reviewed ? (
                <CheckCircleOutlinedIcon
                  sx={{ fontSize: 18, color: 'green' }}
                />
              ) : (
                <WarningAmberIcon color="warning" fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Summary only. The icon above is the control — a text link here was
          too easy to miss for something safety-relevant, and after review it
          offered no way back into what had been decided. */}
      {showDiscordance && (
        <Box sx={{ mt: 0.5 }}>
          {review.reviewed ? (
            <Typography sx={{ fontSize: 11.5, opacity: 0.8 }}>
              {t('Clinician-reviewed')}
              {review.current?.reviewedBy
                ? ` ${t('by {{reviewedBy}}', {
                    reviewedBy: review.current.reviewedBy
                  })}`
                : ''}
              {review.current?.reviewedAt
                ? ` ${t('on {{value}}', {
                    value: moment(review.current.reviewedAt).format(dateFormat)
                  })}`
                : ''}
              {review.history.length > 1
                ? ` · ${t('{{count}} reviews', {
                    count: review.history.length
                  })}`
                : ''}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 11.5, color: 'warning.main' }}>
              {review.stale
                ? t(
                    'A source or the determination has changed since this was reviewed — needs reviewing again'
                  )
                : t('Laboratory and ClinVar do not agree — not yet reviewed')}
            </Typography>
          )}
        </Box>
      )}

      <DiscrepancyReviewDialog
        open={reviewOpen}
        variant={variant}
        reviewerName={reviewerName}
        onCancel={() => setReviewOpen(false)}
        dateFormat={dateFormat}
        onConfirm={({ determination, note }) => {
          setReviewOpen(false);
          if (onReviewDiscrepancy)
            onReviewDiscrepancy(variant.variantId, { determination, note });
        }}
      />

      <ClinvarProvenance provenance={clinvar} />
    </Box>
  );
};

// Inherited vs acquired.
//
// Only rendered when the origin has actually been STATED. An unstated origin
// deliberately shows nothing rather than "germline": the analyzer treats it as
// potentially heritable, and labelling it germline on screen would assert
// something no one recorded.
//
// Somatic is the clinically notable case — it means the variant was acquired
// and cannot have been transmitted, so it is not evidence of an inherited
// cause however pathogenic it is.
const VariantOriginChip = ({ origin }) => {
  const t = useAutoTranslation();

  const value = String(origin || '').toLowerCase();
  if (value !== 'somatic' && value !== 'germline') return null;
  const somatic = value === 'somatic';
  return (
    <Tooltip
      title={
        somatic
          ? t(
              'Recorded as somatic: acquired rather than inherited, so it is not evidence of a transmitted cause.'
            )
          : t('Recorded as germline: potentially transmitted to relatives.')
      }
    >
      <Chip
        size="small"
        variant="outlined"
        label={somatic ? t('Somatic') : t('Germline')}
        sx={{
          height: 18,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.04em'
        }}
      />
    </Tooltip>
  );
};

// ClinVar's star rating. Rendered only when we actually know it — an unknown
// rating must never render as zero stars, which would read as a real
// assessment of low confidence.
const ReviewStars = ({ stars }) => {
  if (!Number.isFinite(stars)) return null;
  return (
    <Box component="span" sx={{ letterSpacing: 1 }}>
      {'★'.repeat(stars)}
      {'☆'.repeat(Math.max(0, 4 - stars))}
    </Box>
  );
};

// Why ClinVar has no classification for this variant. These are different
// clinical statements and must not look alike: an outage is not an absence of
// evidence, and an ambiguous match is not a negative result.
const LOOKUP_STATE_LABEL = {
  lookup_error: 'ClinVar: lookup unavailable',
  ambiguous: 'ClinVar: several records match — none applied',
  insufficient_identifiers: 'ClinVar: needs HGVS notation to match',
  no_match: 'ClinVar: no matching record',
  not_attempted: 'ClinVar: not checked',
  vocabulary_unavailable: 'ClinVar: reference data unavailable',
  system_disabled: 'ClinVar: switched off'
};

const LOOKUP_STATE_TOOLTIP = {
  lookup_error:
    'The ClinVar lookup did not complete. This is not a statement about the variant — it will be retried.',
  ambiguous:
    'More than one ClinVar record matches this notation, so none was applied. Resolve by recording a more specific HGVS notation.',
  insufficient_identifiers:
    'A gene symbol alone cannot identify a variant. Record a c., g. or p. notation to enable a ClinVar match.',
  no_match: 'No ClinVar record matches this variant.',
  not_attempted: 'No ClinVar lookup has been attempted for this variant yet.',
  // Distinct from no_match on purpose: the lookup ran and answered honestly,
  // but out of an empty ClinVar vocabulary, so its answer says nothing about
  // this variant (TGCLOUD-1785).
  vocabulary_unavailable:
    'The ClinVar reference data is not loaded, so this variant could not be checked. This is not a statement about the variant. Please contact an admin.',
  // An admin's switch, not an answer: nothing was asked (TGCLOUD-1892).
  system_disabled:
    'ClinVar is switched off in Settings, Terminology, so this variant was not checked. This is not a statement about the variant.'
};

const ClinvarChip = ({ source, provenance }) => {
  const t = useAutoTranslation();

  const classification = source?.classification;

  if (!classification) {
    const status = provenance?.lookupStatus || 'not_attempted';
    const label =
      LOOKUP_STATE_LABEL[status] || LOOKUP_STATE_LABEL.not_attempted;
    return (
      <Tooltip
        title={
          t(LOOKUP_STATE_TOOLTIP[status]) ||
          t(LOOKUP_STATE_TOOLTIP.not_attempted)
        }
      >
        <Chip
          size="small"
          variant="outlined"
          label={t(label)}
          sx={paleChipSx(
            status === 'lookup_error' ||
              status === 'vocabulary_unavailable' ||
              status === 'system_disabled'
              ? 'uncertain'
              : 'unclassified'
          )}
        />
      </Tooltip>
    );
  }

  // ClinVar's review status is its own published wording, shown as it came.
  const detail = [
    Number.isFinite(provenance.submitterCount)
      ? provenance.submitterCount === 1
        ? t('{{count}} submitter', { count: provenance.submitterCount })
        : t('{{count}} submitters', { count: provenance.submitterCount })
      : null,
    provenance.reviewStatus,
    provenance.lastEvaluated
      ? t('last evaluated {{date}}', { date: provenance.lastEvaluated })
      : null
  ].filter(Boolean);
  const shownClassification = classificationText(classification, t);

  const chip = (
    <Chip
      size="small"
      // The source's own words, verbatim — including compound classifications
      // such as "Pathogenic/Likely pathogenic" and ClinVar's own
      // "Conflicting classifications of pathogenicity", which used to be
      // dropped entirely because they matched no canonical tier.
      label={t('ClinVar: {{classification}}', {
        classification: shownClassification
      })}
      sx={[
        paleChipSx(severityFromInterpretation(classification)),
        provenance.variationId ? { cursor: 'pointer' } : null
      ]}
    />
  );

  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
          <Box>
            <strong>ClinVar:</strong> {shownClassification}
          </Box>
          {provenance.reviewStars !== null && (
            <Box>
              <strong>{t('Review status:')}</strong>{' '}
              <ReviewStars stars={provenance.reviewStars} />
            </Box>
          )}
          {detail.map((d) => (
            <Box key={d}>{d}</Box>
          ))}
          {/* Expert-panel and practice-guideline assertions are labelled
              distinctly: they carry recognised evidentiary weight that a raw
              classification string does not convey. */}
          {provenance.practiceGuideline && (
            <Box sx={{ mt: 0.5 }}>
              <strong>{t('Practice guideline assertion')}</strong>
            </Box>
          )}
          {provenance.expertPanel && (
            <Box sx={{ mt: 0.5 }}>
              <strong>{t('Reviewed by expert panel')}</strong>
            </Box>
          )}
          {provenance.internalConflict && (
            <Box sx={{ mt: 0.5 }}>
              {t('ClinVar reports conflicting submissions for this variant.')}
            </Box>
          )}
          {/* The stored classification stays when ClinVar is switched off,
              and is not refreshed (TGCLOUD-1892). */}
          {provenance.lookupStatus === 'system_disabled' && (
            <Box sx={{ mt: 0.5 }}>
              {t(
                'ClinVar is switched off in Settings, Terminology, so this classification is not being refreshed.'
              )}
            </Box>
          )}
          {provenance.variationId && (
            <Box sx={{ mt: 0.5, fontStyle: 'italic' }}>
              {t('Click to open in ClinVar')}
            </Box>
          )}
        </Box>
      }
    >
      {provenance.variationId ? (
        <a
          href={`https://www.ncbi.nlm.nih.gov/clinvar/variation/${provenance.variationId}/`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none', display: 'inline-flex' }}
        >
          {chip}
        </a>
      ) : (
        chip
      )}
    </Tooltip>
  );
};

// When the displayed value was retrieved, and from which dataset. Without
// this, what a clinician saw on a given date cannot be reconstructed after the
// fact.
//
// BOTH HALVES ARE READ IN UTC, and they have to be (TGCLOUD-1834).
// `clinvarLastFetchedAt` is a server `new Date()` — genotypeVariantService.js
// and variantReclassificationMonitor.js — and the version beside it is written
// by terminology-service's seedClinvar.js as
// `new Date().toISOString().slice(0, 10)`, a UTC calendar date. Read with local
// getters, the line contradicted itself: at UTC−5 it rendered
// 'Retrieved 3 Mar 2026 · ClinVar 2026-03-04', a value retrieved the day before
// the release it came from. It also rendered one audit line as three different
// days depending on where the clinician sat, which defeats the paragraph above.
//
// `reviewedAt` elsewhere in this file is deliberately NOT read this way. It is
// stamped on the USER's clock — a browser `new Date()` in GenotypeModal — so
// local getters are correct for it and moment.utc would be the mirror-image
// bug. `sourceTestDate` is neither: it has a picker writer (a local instant)
// AND a UTC-midnight writer (a confirmed lab report's fill, an imported Test),
// so it is read with toLocalDateFromPickerOrImport. CLAUDE.md: pick the reader
// by the writer, not by the declared type.
//
// A consequence worth knowing: a clinician west of UTC can see a UTC
// 'Retrieved' day above a local 'Clinician-reviewed on' day. Each is the right
// reader for its own field; they are different clocks because they were written
// by different clocks.
const ClinvarProvenance = ({ provenance }) => {
  const t = useAutoTranslation();
  const monthDate = useMonthDate();

  const parts = [
    provenance?.retrievedAt
      ? t('Retrieved {{date}}', {
          date: monthDate(provenance.retrievedAt, 'D MMM YYYY', { utc: true })
        })
      : null,
    provenance?.datasetVersion ? `ClinVar ${provenance.datasetVersion}` : null
  ].filter(Boolean);

  if (parts.length === 0 && !provenance?.researchUseOnly) return null;

  return (
    <Typography sx={{ mt: 0.5, fontSize: 10.5, opacity: 0.65 }}>
      {parts.join(' · ')}
      {provenance?.researchUseOnly && (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {parts.length > 0 ? ' · ' : ''}
          {t('Research use only')}
        </Box>
      )}
    </Typography>
  );
};

const ClinicianSelect = ({ value, onChange, disabled }) => {
  const t = useAutoTranslation();

  const severity = severityFromInterpretation(value);

  return (
    <FormControl size="small" disabled={disabled}>
      <Select
        displayEmpty
        value={value}
        onChange={(e) => onChange(e.target.value)}
        renderValue={(v) =>
          t('Clinician: {{classification}}', {
            classification: v ? classificationText(v, t) : t('None')
          })
        }
        sx={(theme) => ({
          height: 24,
          fontSize: 12,
          ...paleChipSx(severity)(theme),
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
          '& .MuiSelect-select': {
            py: 0,
            pl: 1,
            pr: '24px !important',
            display: 'flex',
            alignItems: 'center'
          }
        })}
      >
        <MenuItem value="">
          <em>{t('None')}</em>
        </MenuItem>
        {VARIANT_INTERPRETATION_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {t(opt.label)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default VariantRow;
